'use client';
import React, { useState, useEffect, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { IconButton } from '@mui/material';
import { UserAuth } from '@/app/context/AuthContext';
import { usePathname, useRouter } from 'next/navigation';
import SearchInput from './SearchInput';
import { useSearchParams } from 'next/navigation';
import InspirationMenu from './InspirationMenu';
import { LoadingLink } from './LoadingLink';
import { GlobalLoader } from './GlobalLoader';
const NavLinksContent = ({ categories: cat }) => {
	const [isLoading, setIsLoading] = useState(false);
	const pathname = usePathname();
	const reduxCategories = useSelector(
		(state) => state.categoryReducer.allCategories
	);
	const categories = cat || reduxCategories;
	const { user, isPro, googleSignIn } = UserAuth();
	const router = useRouter();
	const searchQuery = useSearchParams().get('query');
	const [isHovered, setIsHovered] = useState(false);

	useEffect(() => {
		const subscribeToMailchimp = async () => {
			if (user && user.email) {
				const mailchimpUrl =
					'https://tinytexts.us22.list-manage.com/subscribe/post-json?u=dc310015b31321bdba93b3b1a&id=b9c191733b&c=?';

				const formData = new FormData();
				formData.append('EMAIL', user.email);

				try {
					await fetch(mailchimpUrl, {
						method: 'POST',
						body: formData,
						mode: 'no-cors',
					});
				} catch (error) {
					console.error('Error subscribing to Mailchimp:', error);
				}
			}
		};

		subscribeToMailchimp();
	}, [user]);

	useEffect(() => {
		setIsLoading(false);
	}, [pathname]);

	const handleLogin = async () => {
		try {
			const result = await googleSignIn();
			if (!result?.user) return;

			if (result.user.email) {
				const mailchimpUrl =
					'https://tinytexts.us22.list-manage.com/subscribe/post-json?u=dc310015b31321bdba93b3b1a&id=b9c191733b&c=?';

				const formData = new FormData();
				formData.append('EMAIL', result.user.email);

				await fetch(mailchimpUrl, {
					method: 'POST',
					body: formData,
					mode: 'no-cors',
				});
			}

			router.push('/explore');
		} catch (error) {
			console.error('Error signing in:', error);
		}
	};

	const handleLoadingChange = (loading) => {
		setIsLoading(loading);
	};

	const hideLoader = () => {
		setIsLoading(false);
	};

	return (
		<>
			<GlobalLoader
				isVisible={isLoading}
				onHide={hideLoader}
			/>
			<div className='flex items-center justify-between w-full gap-4'>
				<div className='flex items-center gap-4'>
					<div
						onMouseEnter={() => setIsHovered(true)}
						onMouseLeave={() => setIsHovered(false)}
						style={{ position: 'relative' }}
					>
						<button className='text-[#020314] md:text-xl md:font-normal focus:outline-none'>
							Categories
						</button>
						<div
							style={{
								position: 'absolute',
								top: '100%',
								left: 0,
								right: 0,
								height: '18px',
								zIndex: 49,
							}}
						/>
						<div
							style={{
								display: isHovered ? 'block' : 'none',
								position: 'absolute',
								backgroundColor: '#00BFFF',
								zIndex: 50,
								color: '#000',
								left: '-15rem',
								top: '100%', // Added: positions dropdown right below the button
								marginTop: '18px', // Added: adds 8px gap between button and dropdown
								width: 'calc(100vw + 15rem)',
							}}
						>
							<div className='flex gap-10 px-12 py-4'>
								<div className='flex gap-10 px-12 py-4'>
									{Array.isArray(categories) &&
										categories.length > 0 &&
										Array.from({
											length: Math.ceil(categories.length / 6),
										}).map((_, index) => (
											<div
												key={index}
												className='flex flex-col gap-2'
											>
												{categories
													.slice(index * 6, index * 6 + 6)
													.map((cat, subIndex) => {
														const catHref = cat?.slug || cat?.categoryId
															? `/categories/${cat.slug || cat.categoryId}`
															: null;
														return catHref ? (
															<LoadingLink
																key={cat.categoryId || subIndex}
																href={catHref}
																className='block text-sm font-normal text-left text-black transition-colors duration-200 md:text-xl hover:underline whitespace-nowrap'
																onLoadingChange={handleLoadingChange}
															>
																{cat?.name || 'Unnamed Category'}
															</LoadingLink>
														) : (
															<span
																key={cat?.categoryId || subIndex}
																className='block text-sm font-normal text-left text-gray-400 md:text-xl cursor-not-allowed opacity-60'
															>
																{cat?.name || 'Unnamed Category'}
															</span>
														);
													})}
											</div>
										))}
								</div>
							</div>
						</div>
					</div>

					{/* Brands */}
					<div className='mr-4'>
						<LoadingLink
							href='/brands'
							style={{ all: 'unset' }}
							onLoadingChange={handleLoadingChange}
						>
							<IconButton sx={{ borderRadius: '4px' }}>
								<p className='text-[#020314] md:text-xl md:font-normal'>
									Brands
								</p>
							</IconButton>
						</LoadingLink>
					</div>
					<InspirationMenu setIsLoading={setIsLoading} />
				</div>

				{pathname !== '/' && (
					<div className='flex-1 min-w-0 max-w-lg'>
						<SearchInput
							defaultValue={searchQuery}
							navigateToSearchPage
						/>
					</div>
				)}
				<div className='flex items-center gap-4 flex-shrink-0 ml-auto max-lg:flex-col'>
					{!user ? (
						<>
							<button
								onClick={handleLogin}
								className='rounded-lg p-1 bg-white lg:px-5 lg:py-2 text-base font-semibold text-black shadow-sm hover:bg-gray-100 whitespace-nowrap'
							>
								Join for free
							</button>
							<button
								onClick={handleLogin}
								className='text-base font-normal max-lg:flex-wrap text-black hover:underline'
							>
								Sign In
							</button>
						</>
					) : (
						<button
							onClick={isPro ? () => router.push('/account') : () => router.push('/pro')}
							className='rounded-lg bg-[#AF52DE] px-5 py-2 text-base font-semibold text-white shadow-sm hover:bg-[#9a3fc6]'
						>
							{isPro ? 'PRO ✓' : 'Get PRO'}
						</button>
					)}
				</div>
			</div>
		</>
	);
};

const NavLinks = (props) => (
	<Suspense fallback={null}>
		<NavLinksContent {...props} />
	</Suspense>
);

export default NavLinks;
